import { useState, useEffect, useRef, useCallback, useId, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs';
import 'prismjs/components/prism-json';
import 'prismjs/themes/prism-tomorrow.css';
import {
  X,
  Plus,
  Trash2,
  Pencil,
  Check,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Copy,
  User,
  Globe,
  ExternalLink,
  Play,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { cn } from '@/lib/utils';
import FormattedDate from '@/components/common/FormattedDate';
import {
  useWorkflowTemplate,
  useTemplateValidation,
  useUpdateWorkflowTemplate,
  useDeleteWorkflowTemplate,
  useCreateTemplateVariable,
  useUpdateTemplateVariable,
  useDeleteTemplateVariable,
  useWorkflowVariableTypes,
} from '@/hooks/useWorkflows';
import type { WorkflowTemplate, WorkflowEnvironment, WorkflowVariable } from '@/lib/types';
import CopyVariablesModal from './CopyVariablesModal';
import StartJobModal from './StartJobModal';

interface Props {
  template: WorkflowTemplate;
  onClose: () => void;
}

export default function TemplateDetailModal({ template: initialTemplate, onClose }: Props) {
  const [activeTab, setActiveTab] = useState<'details' | 'variables' | 'body'>('details');
  const [showAddVariable, setShowAddVariable] = useState(false);
  const [editingVariable, setEditingVariable] = useState<WorkflowVariable | null>(null);
  const [showCopyVariables, setShowCopyVariables] = useState(false);
  const [showStartJob, setShowStartJob] = useState(false);
  const variableFormRef = useRef<HTMLDivElement>(null);

  // Form state for add/edit variable
  const [varName, setVarName] = useState('');
  const [varType, setVarType] = useState('string');
  const [varRequired, setVarRequired] = useState(false);
  const [varDefault, setVarDefault] = useState('');
  const [varDescription, setVarDescription] = useState('');
  const [varValidatorJson, setVarValidatorJson] = useState('');
  const [validatorError, setValidatorError] = useState<string | null>(null);

  // Body editing state. bodyDraft holds the user's in-progress edit, or null when there is none:
  // then the editor shows the server body directly, so a background refetch is reflected without an
  // effect and a live draft survives a same-template refetch. editedBody/isBodyDirty are derived
  // from bodyDraft + the current server body below.
  const [bodyDraft, setBodyDraft] = useState<string | null>(null);
  const [bodySaveError, setBodySaveError] = useState<string | null>(null);
  const [isSavingBody, setIsSavingBody] = useState(false);

  const environmentSelectId = useId();
  const versionCaptionId = useId();
  const descriptionCaptionId = useId();

  // Inline editing state for details fields
  const [isEditingVersion, setIsEditingVersion] = useState(false);
  const [editedVersion, setEditedVersion] = useState(initialTemplate.version);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState(initialTemplate.description || '');

  // Generate validator template from type's constraints
  const generateValidatorTemplate = (typeName: string): string => {
    const typeInfo = variableTypes.find(t => t.name === typeName);
    if (!typeInfo?.constraints?.length) return '';
    const template: Record<string, null> = {};
    for (const constraint of typeInfo.constraints) {
      template[constraint] = null;
    }
    return JSON.stringify(template, null, 2);
  };

  // Parse and validate JSON, returns parsed object (filtering nulls) or undefined if empty/invalid
  const parseValidatorJson = (json: string): Record<string, unknown> | undefined => {
    const trimmed = json.trim();
    if (!trimmed) return undefined;
    try {
      const parsed = JSON.parse(trimmed) as Record<string, unknown>;
      // Filter out null values
      const filtered: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(parsed)) {
        if (value !== null) {
          filtered[key] = value;
        }
      }
      // Return object even if empty (allows clearing via {} template)
      return filtered;
    } catch {
      return undefined;
    }
  };

  const { data: template } = useWorkflowTemplate(initialTemplate.id);
  const { data: validation } = useTemplateValidation(initialTemplate.id);
  const { data: varTypesData } = useWorkflowVariableTypes();

  const updateTemplate = useUpdateWorkflowTemplate();
  const deleteTemplate = useDeleteWorkflowTemplate();
  const createVariable = useCreateTemplateVariable();
  const updateVariable = useUpdateTemplateVariable();
  const deleteVariable = useDeleteTemplateVariable();
  const queryClient = useQueryClient();

  const currentTemplate = template || initialTemplate;
  const variables = currentTemplate.variables || [];
  const variableTypes = varTypesData?.items || [];

  // Derive the body editor's value and dirty flag during render (no sync effect). With no draft the
  // editor tracks the server body; a draft overrides it and is dirty only while it differs from the
  // current server body, so a refetch that catches up to the draft clears the dirty state on its own.
  const editedBody = bodyDraft ?? currentTemplate.body;
  const isBodyDirty = bodyDraft !== null && bodyDraft !== currentTemplate.body;

  // Close with unsaved changes check (used by X button, Close button, ESC, tab switch)
  const handleCloseWithDirtyCheck = useCallback(() => {
    if (isBodyDirty) {
      if (confirm('You have unsaved changes to the template body. Discard changes?')) {
        onClose();
      }
    } else {
      onClose();
    }
  }, [isBodyDirty, onClose]);

  // Save body handler (defined early for keyboard shortcut useEffect)
  const handleSaveBody = useCallback(async () => {
    setIsSavingBody(true);
    setBodySaveError(null);

    // No JSON.parse here - bodies have unquoted <var> placeholders
    // that aren't valid JSON. Same as creation flow.

    // Snapshot the exact text being saved so a keystroke landing mid-request isn't discarded below.
    const savedBody = editedBody;
    try {
      await updateTemplate.mutateAsync({
        id: currentTemplate.id,
        payload: { body: savedBody },
      });
      // The mutation only invalidates the detail query, so the cached body is still the pre-save
      // value until the refetch lands. Seed the saved body into the cache now so dropping the draft
      // shows the saved text immediately instead of flashing the old body. Fall back to
      // currentTemplate when the detail query hasn't populated its cache yet.
      queryClient.setQueryData<WorkflowTemplate>(
        ['workflow-template', currentTemplate.id],
        (prev) => ({ ...(prev ?? currentTemplate), body: savedBody }),
      );
      // Only drop the draft if the user hasn't typed since the save started; otherwise keep their
      // newer edit so it isn't silently lost.
      setBodyDraft((current) => (current === savedBody ? null : current));
      toast.success('Template body updated');
    } catch (err) {
      // API client throws: Error("API Error: {status} - {json body}")
      // Extract the actual message from the embedded JSON
      let message = 'Failed to update body';
      if (err instanceof Error) {
        // Try to extract JSON from "API Error: 422 - {...}"
        const jsonMatch = err.message.match(/- (\{.+})$/);
        if (jsonMatch) {
          try {
            const parsed = JSON.parse(jsonMatch[1]);
            // ValidationError returns {loc, type, code, msg} directly (not wrapped in detail)
            message = parsed?.msg || parsed?.detail?.msg || err.message;
          } catch {
            message = err.message;
          }
        } else {
          message = err.message;
        }
      }
      setBodySaveError(message);
    } finally {
      setIsSavingBody(false);
    }
  }, [currentTemplate, editedBody, updateTemplate, queryClient]);

  const handleCancelVersionEdit = useCallback(() => {
    setEditedVersion(currentTemplate.version);
    setIsEditingVersion(false);
  }, [currentTemplate.version]);

  const handleCancelDescriptionEdit = useCallback(() => {
    setEditedDescription(currentTemplate.description || '');
    setIsEditingDescription(false);
  }, [currentTemplate.description]);

  // Handle keyboard shortcuts (skip when a child modal is open)
  useEffect(() => {
    if (showStartJob || showCopyVariables) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      // ESC: cancel active inline edit first (only on Details tab), then close modal
      if (e.key === 'Escape') {
        if (activeTab === 'details' && isEditingVersion) { handleCancelVersionEdit(); }
        else if (activeTab === 'details' && isEditingDescription) { handleCancelDescriptionEdit(); }
        else { handleCloseWithDirtyCheck(); }
      }
      // Ctrl/Cmd+S to save body (when on body tab)
      if ((e.ctrlKey || e.metaKey) && e.key === 's' && activeTab === 'body' && isBodyDirty && !isSavingBody) {
        e.preventDefault();
        void handleSaveBody();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isBodyDirty, activeTab, isSavingBody, handleSaveBody, handleCloseWithDirtyCheck,
      showStartJob, showCopyVariables, isEditingVersion, isEditingDescription,
      handleCancelVersionEdit, handleCancelDescriptionEdit]);

  const resetVarForm = () => {
    setVarName('');
    setVarType('string');
    setVarRequired(false);
    setVarDefault('');
    setVarDescription('');
    setVarValidatorJson('');
    setValidatorError(null);
  };

  const handleToggleDisable = async () => {
    try {
      await updateTemplate.mutateAsync({
        id: currentTemplate.id,
        payload: {
          disable_ts: currentTemplate.disable_ts ? null : new Date().toISOString(),
        },
      });
      toast.success(currentTemplate.disable_ts ? 'Template enabled' : 'Template disabled');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update template');
    }
  };

  const handleEnvironmentChange = async (newEnv: WorkflowEnvironment) => {
    if (updateTemplate.isPending) return;
    if (newEnv === currentTemplate.environment) return;
    if (newEnv === 'production') {
      if (!confirm('Move this template to production? It will become available for live workloads.')) {
        return;
      }
    }
    try {
      await updateTemplate.mutateAsync({
        id: currentTemplate.id,
        payload: { environment: newEnv },
      });
      toast.success(`Environment changed to ${newEnv}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update environment');
    }
  };

  const handleSaveVersion = async () => {
    if (updateTemplate.isPending) return;
    const trimmed = editedVersion.trim();
    if (!trimmed) {
      toast.error('Version cannot be empty');
      return;
    }
    if (trimmed.length > 16) {
      toast.error('Version must be 16 characters or less');
      return;
    }
    try {
      await updateTemplate.mutateAsync({
        id: currentTemplate.id,
        payload: { version: trimmed },
      });
      setIsEditingVersion(false);
      toast.success('Version updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update version');
    }
  };

  const handleSaveDescription = async () => {
    if (updateTemplate.isPending) return;
    const trimmed = editedDescription.trim();
    if (trimmed.length > 1000) {
      toast.error('Description must be 1000 characters or less');
      return;
    }
    try {
      await updateTemplate.mutateAsync({
        id: currentTemplate.id,
        payload: { description: trimmed || null },
      });
      setIsEditingDescription(false);
      toast.success('Description updated');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update description');
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this template? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteTemplate.mutateAsync(currentTemplate.id);
      toast.success('Template deleted');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete template');
    }
  };

  const handleAddVariable = async (e: FormEvent) => {
    e.preventDefault();

    // Validate JSON if provided
    if (varValidatorJson.trim()) {
      try {
        JSON.parse(varValidatorJson);
        setValidatorError(null);
      } catch {
        setValidatorError('Invalid JSON syntax');
        return;
      }
    }

    try {
      await createVariable.mutateAsync({
        templateId: currentTemplate.id,
        payload: {
          name: varName,
          type_name: varType,
          is_required: varRequired,
          default_value: varDefault || undefined,
          validator: parseValidatorJson(varValidatorJson),
          description: varDescription || undefined,
        },
      });
      toast.success('Variable added');
      setShowAddVariable(false);
      resetVarForm();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to add variable');
    }
  };

  const handleUpdateVariable = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingVariable) return;

    const trimmedValidator = varValidatorJson.trim();

    // Validate JSON if provided
    if (trimmedValidator) {
      try {
        JSON.parse(trimmedValidator);
        setValidatorError(null);
      } catch {
        setValidatorError('Invalid JSON syntax');
        return;
      }
    }

    // Determine validator value: empty editor + existing validator => null to clear
    const validatorValue = !trimmedValidator && editingVariable.validator
      ? null
      : parseValidatorJson(varValidatorJson);

    try {
      await updateVariable.mutateAsync({
        templateId: currentTemplate.id,
        variableId: editingVariable.id,
        payload: {
          type_name: varType,
          is_required: varRequired,
          default_value: varDefault || undefined,
          validator: validatorValue,
          description: varDescription || undefined,
        },
      });
      toast.success('Variable updated');
      setEditingVariable(null);
      resetVarForm();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update variable');
    }
  };

  const handleDeleteVariable = async (variableId: string, variableName: string) => {
    if (!confirm(`Delete variable "${variableName}"?`)) return;

    try {
      await deleteVariable.mutateAsync({
        templateId: currentTemplate.id,
        variableId,
      });
      toast.success('Variable deleted');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete variable');
    }
  };

  const startEditVariable = (variable: WorkflowVariable) => {
    setEditingVariable(variable);
    setVarName(variable.name);
    setVarType(variable.type_name);
    setVarRequired(variable.is_required);
    setVarDefault(variable.default_value || '');
    setVarDescription(variable.description || '');
    // Populate validator JSON from existing validator
    if (variable.validator && Object.keys(variable.validator).length > 0) {
      setVarValidatorJson(JSON.stringify(variable.validator, null, 2));
    } else {
      setVarValidatorJson('');
    }
    setValidatorError(null);
    setShowAddVariable(false);
    // Scroll to form after state update
    setTimeout(() => {
      variableFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 0);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleBodyChange = (newBody: string) => {
    // Collapse a revert-to-server-body to "no draft" so the editor tracks later refetches — but NOT
    // mid-save: while a save is in flight the cached body is still the pre-save value, so collapsing
    // a raced revert to null would make it indistinguishable from "no edit" and silently drop it when
    // the save resolves. Keep it as a real draft while saving.
    setBodyDraft(newBody === currentTemplate.body && !isSavingBody ? null : newBody);
    setBodySaveError(null);
  };

  const handleDiscardBodyChanges = () => {
    setBodyDraft(null);
    setBodySaveError(null);
  };

  const envBadgeStyles: Record<WorkflowEnvironment, string> = {
    production: 'bg-green-500/20 text-green-400 border-green-500/30',
    staging: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    development: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  };

  const renderVariableForm = (isEdit: boolean) => (
    <form
      onSubmit={isEdit ? handleUpdateVariable : handleAddVariable}
      className={cn(
        'p-4 rounded-lg border space-y-3',
        isEdit ? 'bg-blue-500/10 border-blue-500/30' : 'bg-muted/30 border-border'
      )}
    >
      <h3 className="text-sm font-medium">
        {isEdit ? `Edit: ${editingVariable?.name}` : 'Add Variable'}
      </h3>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="var-name" className="text-xs font-medium">Name</label>
          <input
            id="var-name"
            type="text"
            value={varName}
            onChange={(e) => setVarName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
            placeholder="variable_name"
            className="w-full mt-1 px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 font-mono text-sm disabled:opacity-50"
            required
            disabled={isEdit}
          />
        </div>
        <div>
          <label htmlFor="var-type" className="text-xs font-medium">Type</label>
          <select
            id="var-type"
            value={varType}
            onChange={(e) => {
              const newType = e.target.value;
              setVarType(newType);
              // Generate template for new type (only if validator is empty or was a template with all nulls)
              const parsed = parseValidatorJson(varValidatorJson);
              const isEmptyOrTemplate = !varValidatorJson.trim() || (parsed && Object.keys(parsed).length === 0);
              if (isEmptyOrTemplate) {
                setVarValidatorJson(generateValidatorTemplate(newType));
              }
              setValidatorError(null);
            }}
            className="w-full mt-1 px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
            disabled={variableTypes.length === 0}
          >
            {variableTypes.length === 0 && (
              <option value="">Loading types...</option>
            )}
            {variableTypes.map((vt) => (
              <option key={vt.name} value={vt.name}>
                {vt.name} {vt.description && `- ${vt.description}`}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="var-default" className="text-xs font-medium">Default Value</label>
          <input
            id="var-default"
            type="text"
            value={varDefault}
            onChange={(e) => setVarDefault(e.target.value)}
            placeholder="Optional default"
            className="w-full mt-1 px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50 font-mono text-sm"
          />
        </div>
        <div>
          <label htmlFor="var-description" className="text-xs font-medium">Description</label>
          <input
            id="var-description"
            type="text"
            value={varDescription}
            onChange={(e) => setVarDescription(e.target.value)}
            placeholder="Optional description"
            className="w-full mt-1 px-3 py-2 rounded-md bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="var-required"
          checked={varRequired}
          onChange={(e) => setVarRequired(e.target.checked)}
          className="rounded"
        />
        <label htmlFor="var-required" className="text-sm">
          Required
        </label>
      </div>
      {/* Validator JSON editor - only show for types with constraints */}
      {(() => {
        const selectedType = variableTypes.find(t => t.name === varType);
        if (!selectedType?.constraints?.length) return null;
        return (
          <div className="space-y-2 pt-2 border-t border-border/50">
            <div className="flex items-center justify-between">
              <label htmlFor="var-validator-json" className="text-xs font-medium text-muted-foreground">
                Validator (JSON Schema)
              </label>
              <div className="flex items-center gap-2">
                {!varValidatorJson.trim() && (
                  <>
                    <button
                      type="button"
                      onClick={() => setVarValidatorJson(generateValidatorTemplate(varType))}
                      className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                    >
                      Load template
                    </button>
                    <span className="text-border">·</span>
                  </>
                )}
                <a
                  href="https://json-schema.org/understanding-json-schema/reference"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  JSON Schema docs
                </a>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Set constraints like minimum, maximum, pattern, etc. Delete properties you don't need.
            </p>
            <div className={cn(
              'rounded-md border overflow-hidden font-mono text-sm',
              validatorError ? 'border-red-500' : 'border-border'
            )}>
              <Editor
                textareaId="var-validator-json"
                value={varValidatorJson}
                onValueChange={(code) => {
                  setVarValidatorJson(code);
                  setValidatorError(null);
                }}
                highlight={(code) => highlight(code || '', languages.json, 'json')}
                padding={12}
                style={{
                  backgroundColor: 'hsl(var(--background))',
                  minHeight: '80px',
                }}
                placeholder="Empty = no validation constraints"
              />
            </div>
            {validatorError && (
              <p className="text-xs text-red-500">{validatorError}</p>
            )}
          </div>
        );
      })()}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => {
            if (isEdit) {
              setEditingVariable(null);
            } else {
              setShowAddVariable(false);
            }
            resetVarForm();
          }}
          className="px-3 py-1.5 rounded-md bg-secondary hover:bg-secondary/80 text-sm"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isEdit ? updateVariable.isPending : createVariable.isPending}
          className="px-3 py-1.5 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground text-sm disabled:opacity-50"
        >
          {isEdit
            ? updateVariable.isPending
              ? 'Saving...'
              : 'Save'
            : createVariable.isPending
              ? 'Adding...'
              : 'Add Variable'}
        </button>
      </div>
    </form>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="template-modal-title"
    >
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <h2 id="template-modal-title" className="text-xl font-semibold">
              {currentTemplate.type_name} / {currentTemplate.subtype}
            </h2>
            <span className="text-sm text-muted-foreground">v{currentTemplate.version}</span>
            <span
              className={cn(
                'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border',
                envBadgeStyles[currentTemplate.environment]
              )}
            >
              {currentTemplate.environment}
            </span>
            {currentTemplate.customer_id ? (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                <User className="h-3 w-3" />
                Customer-specific
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                <Globe className="h-3 w-3" />
                Global
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={handleCloseWithDirtyCheck}
            className="p-1 rounded-md hover:bg-muted transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border" role="tablist" aria-label="Template sections">
          {(['details', 'variables', 'body'] as const).map((tab) => (
            <button
              key={tab}
              id={`modal-tab-${tab}`}
              type="button"
              role="tab"
              aria-selected={activeTab === tab}
              aria-controls={`modal-panel-${tab}`}
              tabIndex={activeTab === tab ? 0 : -1}
              onClick={() => {
                if (activeTab === 'body' && isBodyDirty && tab !== 'body') {
                  if (!confirm('You have unsaved changes to the template body. Discard changes?')) {
                    return;
                  }
                  handleDiscardBodyChanges();
                }
                setActiveTab(tab);
              }}
              className={cn(
                'px-6 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === tab
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tab === 'variables' && ` (${variables.length})`}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'details' && (
            <div
              id="modal-panel-details"
              role="tabpanel"
              aria-labelledby="modal-tab-details"
              className="space-y-6"
            >
              {/* Properties */}
              <div className="rounded-lg border border-border">
                <div className="px-4 py-3 border-b border-border/50">
                  <h3 className="text-sm font-medium">Properties</h3>
                </div>
                <div className="divide-y divide-border/50">
                  {/* Environment */}
                  <div className="flex items-center justify-between px-4 py-3">
                    <label htmlFor={environmentSelectId} className="text-sm text-muted-foreground">Environment</label>
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        'h-2.5 w-2.5 rounded-full',
                        currentTemplate.environment === 'production' && 'bg-green-400',
                        currentTemplate.environment === 'staging' && 'bg-yellow-400',
                        currentTemplate.environment === 'development' && 'bg-blue-400',
                      )} />
                      <select
                        id={environmentSelectId}
                        value={currentTemplate.environment}
                        onChange={(e) => handleEnvironmentChange(e.target.value as WorkflowEnvironment)}
                        disabled={updateTemplate.isPending}
                        className="px-2 py-1 rounded-md bg-muted/50 border border-border text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="development" className="bg-white text-gray-900 dark:bg-zinc-800 dark:text-gray-100">development</option>
                        <option value="staging" className="bg-white text-gray-900 dark:bg-zinc-800 dark:text-gray-100">staging</option>
                        <option value="production" className="bg-white text-gray-900 dark:bg-zinc-800 dark:text-gray-100">production</option>
                      </select>
                    </div>
                  </div>

                  {/* Version */}
                  <div className="flex items-center justify-between px-4 py-3">
                    <span id={versionCaptionId} className="text-sm text-muted-foreground">Version</span>
                    {isEditingVersion ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editedVersion}
                          onChange={(e) => setEditedVersion(e.target.value)}
                          aria-labelledby={versionCaptionId}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); void handleSaveVersion(); }
                          }}
                          maxLength={16}
                          autoFocus
                          className="w-40 px-2 py-1 rounded-md bg-background border border-border text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/50"
                        />
                        <span className="text-xs text-muted-foreground">{editedVersion.length}/16</span>
                        <button
                          type="button"
                          onClick={() => void handleSaveVersion()}
                          disabled={updateTemplate.isPending}
                          className="p-1 rounded-md hover:bg-green-500/20 text-green-400 transition-colors disabled:opacity-50"
                          title="Save"
                          aria-label="Save version"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelVersionEdit}
                          className="p-1 rounded-md hover:bg-muted transition-colors"
                          title="Cancel"
                          aria-label="Cancel version edit"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-mono">v{currentTemplate.version}</span>
                        <button
                          type="button"
                          onClick={() => {
                            setEditedVersion(currentTemplate.version);
                            setIsEditingVersion(true);
                          }}
                          className="p-1 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                          title="Edit version"
                          aria-label="Edit version"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <div className="px-4 py-3">
                    <div className="flex items-center justify-between mb-1">
                      <span id={descriptionCaptionId} className="text-sm text-muted-foreground">Description</span>
                      {!isEditingDescription && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditedDescription(currentTemplate.description || '');
                            setIsEditingDescription(true);
                          }}
                          className="p-1 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                          title="Edit description"
                          aria-label="Edit description"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                    {isEditingDescription ? (
                      <div className="space-y-2">
                        <textarea
                          value={editedDescription}
                          onChange={(e) => setEditedDescription(e.target.value)}
                          aria-labelledby={descriptionCaptionId}
                          maxLength={1000}
                          rows={3}
                          autoFocus
                          className="w-full px-3 py-2 rounded-md bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-y"
                          placeholder="Add a description..."
                        />
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">
                            {editedDescription.length}/1000
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={handleCancelDescriptionEdit}
                              className="px-3 py-1 rounded-md text-sm hover:bg-muted transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={() => void handleSaveDescription()}
                              disabled={updateTemplate.isPending}
                              className="px-3 py-1 rounded-md text-sm bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className={cn(
                        'text-sm',
                        currentTemplate.description ? '' : 'text-muted-foreground italic'
                      )}>
                        {currentTemplate.description || 'No description'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Status & Validation */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-muted/30 border border-border">
                  <h3 className="text-sm font-medium mb-2">Status</h3>
                  {currentTemplate.disable_ts ? (
                    <div className="flex items-center gap-2 text-red-400">
                      <XCircle className="h-5 w-5" />
                      <span>Disabled</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-green-400">
                      <CheckCircle className="h-5 w-5" />
                      <span>Active</span>
                    </div>
                  )}
                </div>
                <div className="p-4 rounded-lg bg-muted/30 border border-border">
                  <h3 className="text-sm font-medium mb-2">Validation</h3>
                  {validation?.is_valid ? (
                    <div className="flex items-center gap-2 text-green-400">
                      <CheckCircle className="h-5 w-5" />
                      <span>Valid</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-yellow-400">
                      <AlertTriangle className="h-5 w-5" />
                      <span>
                        {validation?.missing_definitions?.length || 0} undefined variables
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Metadata */}
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-muted-foreground">ID</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm">{currentTemplate.id}</span>
                    <button
                      type="button"
                      onClick={() => copyToClipboard(currentTemplate.id)}
                      className="p-1 hover:bg-muted rounded"
                      aria-label="Copy template ID"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-muted-foreground">Created</span>
                  <FormattedDate date={currentTemplate.create_ts} />
                </div>
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-muted-foreground">Updated</span>
                  <FormattedDate date={currentTemplate.update_ts} />
                </div>
              </div>

              {/* Validation Details */}
              {validation && !validation.is_valid && (
                <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                  <h3 className="text-sm font-medium text-yellow-400 mb-2">Missing Definitions</h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    The following placeholders in the body don't have variable definitions:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {validation.missing_definitions?.map((name) => (
                      <code key={name} className="px-2 py-1 bg-muted rounded text-sm font-mono">
                        &lt;{name}&gt;
                      </code>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'variables' && (
            <div
              id="modal-panel-variables"
              role="tabpanel"
              aria-labelledby="modal-tab-variables"
              className="space-y-4"
            >
              {/* Add Variable Form */}
              {showAddVariable && !editingVariable && renderVariableForm(false)}

              {/* Edit Variable Form */}
              {editingVariable && (
                <div ref={variableFormRef}>
                  {renderVariableForm(true)}
                </div>
              )}

              {/* Add / Copy Buttons (when not showing forms) */}
              {!showAddVariable && !editingVariable && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddVariable(true);
                      resetVarForm();
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-colors text-sm"
                  >
                    <Plus className="h-4 w-4" />
                    Add Variable
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCopyVariables(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded-md bg-muted hover:bg-muted/80 text-foreground border border-border transition-colors text-sm"
                  >
                    <Copy className="h-4 w-4" />
                    Copy from...
                  </button>
                </div>
              )}

              {/* Variables List */}
              {variables.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No variables defined</p>
                  <p className="text-sm mt-1">
                    Add variables to define types and validation for template placeholders
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {variables.map((variable) => (
                    <div
                      key={variable.id}
                      className={cn(
                        'p-3 rounded-lg border transition-colors',
                        editingVariable?.id === variable.id
                          ? 'bg-blue-500/5 border-blue-500/30'
                          : 'bg-muted/30 border-border hover:bg-muted/50'
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <code className="font-mono text-sm bg-muted px-2 py-1 rounded">
                            &lt;{variable.name}&gt;
                          </code>
                          <span className="text-sm text-muted-foreground">{variable.type_name}</span>
                          {variable.is_required && (
                            <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">
                              Required
                            </span>
                          )}
                          {variable.default_value && (
                            <span className="text-xs text-muted-foreground">
                              default: <code className="font-mono">{variable.default_value}</code>
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => startEditVariable(variable)}
                            className="p-1.5 rounded-md hover:bg-muted transition-colors"
                            title="Edit"
                            aria-label={`Edit variable ${variable.name}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteVariable(variable.id, variable.name)}
                            className="p-1.5 rounded-md hover:bg-red-500/20 text-red-400 transition-colors"
                            title="Delete"
                            aria-label={`Delete variable ${variable.name}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      {variable.description && (
                        <p className="text-xs text-muted-foreground mt-2 pl-2 border-l-2 border-border">
                          {variable.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'body' && (
            <div
              id="modal-panel-body"
              role="tabpanel"
              aria-labelledby="modal-tab-body"
              className="space-y-4"
            >
              {/* Header with actions */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Edit the workflow JSON. Variables use{' '}
                  <code className="bg-muted px-1 rounded">&lt;variable_name&gt;</code> syntax.
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => copyToClipboard(editedBody)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-secondary hover:bg-secondary/80 text-sm"
                  >
                    <Copy className="h-3.5 w-3.5" />
                    Copy
                  </button>
                  {isBodyDirty && (
                    <button
                      type="button"
                      onClick={handleDiscardBodyChanges}
                      className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
                      disabled={isSavingBody}
                    >
                      Discard
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleSaveBody}
                    disabled={!isBodyDirty || isSavingBody}
                    className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Save changes (Ctrl+S)"
                  >
                    {isSavingBody ? 'Saving...' : 'Save Body'}
                  </button>
                </div>
              </div>

              {/* Error display — hide once the body is no longer dirty (e.g. a refetch caught the
                  server up to the draft), since a stale save error would otherwise linger. */}
              {bodySaveError && isBodyDirty && (
                <div role="alert" aria-live="assertive" aria-atomic="true" className="p-3 bg-red-500/10 border border-red-500/30 rounded-md">
                  <p className="text-sm text-red-400 font-medium">Cannot save changes</p>
                  <p className="text-sm text-red-400/80 mt-1">{bodySaveError}</p>
                  {bodySaveError.toLowerCase().includes('variable') && (
                    <p className="text-sm text-muted-foreground mt-2">
                      To change variables, create a new template version instead.
                    </p>
                  )}
                </div>
              )}

              {/* JSON Editor */}
              <div className={cn(
                'rounded-md border overflow-hidden',
                bodySaveError && isBodyDirty ? 'border-red-500' : 'border-border'
              )}>
                <Editor
                  textareaId="template-body-editor"
                  aria-label="Template body editor"
                  value={editedBody}
                  onValueChange={handleBodyChange}
                  highlight={(code) => highlight(code || '', languages.json, 'json')}
                  padding={12}
                  style={{
                    fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                    fontSize: '13px',
                    minHeight: '400px',
                    backgroundColor: 'hsl(var(--background))',
                  }}
                />
              </div>

              {/* Dirty indicator */}
              {isBodyDirty && !bodySaveError && (
                <p className="text-sm text-amber-500">You have unsaved changes</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleteTemplate.isPending}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 transition-colors disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setShowStartJob(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 transition-colors"
            >
              <Play className="h-4 w-4" />
              Start Job
            </button>
            <button
              type="button"
              onClick={handleToggleDisable}
              disabled={updateTemplate.isPending}
              className={cn(
                'px-4 py-2 rounded-md border transition-colors disabled:opacity-50',
                currentTemplate.disable_ts
                  ? 'bg-green-500/10 hover:bg-green-500/20 text-green-400 border-green-500/30'
                  : 'bg-yellow-500/10 hover:bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
              )}
            >
              {currentTemplate.disable_ts ? 'Enable Template' : 'Disable Template'}
            </button>
            <button
              type="button"
              onClick={handleCloseWithDirtyCheck}
              className="px-4 py-2 rounded-md bg-secondary hover:bg-secondary/80 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {showCopyVariables && (
        <CopyVariablesModal
          destTemplate={currentTemplate}
          onClose={() => setShowCopyVariables(false)}
        />
      )}

      {showStartJob && (
        <StartJobModal
          template={currentTemplate}
          onClose={() => setShowStartJob(false)}
        />
      )}
    </div>
  );
}
